<template>
  <div>
    <div v-if="singleForm" id="basicInfoQuestionBooleanIcons">
      <div class="text-center">Select all that apply</div>
      <div class="multi-bool-single-form d-flex flex-wrap justify-content-between mb-2">
        <label
          v-for="(option, index) in options"
          :key="index"
          :for="'option-' + index"
          @keydown.enter="onEnter(options)"
          @keydown.space="toggleValue(option, $event)"
        >
          <div class="single-form-option-box option-box text-center"
               :class="{
              'option-box--checked': option.value,
              'option-box--focused': option.focused,
              'option-box--invalid': v$ && v$.options.$error,
            }">
            <input type="checkbox"
                   class="option-box__check position-absolute left-0 right-0 top-0 bottom-0 m-auto"
                   :id="'option-' + index"
                   :name="'option-' + index"
                   :dreams-id="question.attributes[index].alias"
                   @focus="onFocus(option)"
                   @blur="onBlur(v$, option)"
                   @change="singleFormChangeValues(options)"
                   @invalidity="$emit('invalidity', $event)"
                   :required="question.required"
                   v-model="option.value"
            >
            <div class="boolean-icon-container mt-0 mx-auto mb-3 position-relative">
              <transition name="fade">
                <i v-show="option.value" class="position-absolute top-0 start-0 bottom-0 end-0 m-auto" :class="getOptionInfo(option).icon2"></i>
              </transition>
              <transition name="fade">
                <i v-show="!option.value" class="position-absolute top-0 start-0 bottom-0 end-0 m-auto" :class="getOptionInfo(option).icon"></i>
              </transition>
            </div>

            <small class="option-box__label">
              {{ getOptionInfo(option).label }}
            </small>
          </div>
        </label>
        <label
          @keydown.space="noneApply(options, $event)"
          @click="noneApply(options, $event)"
        >
          <div class="single-form-option-box option-box text-center"
               :class="{
              'option-box--checked': noneApplySelected,
              'option-box--focused': noneApplyOption.focused,
              'option-box--invalid': v$.options.$error,
            }">
            <input type="checkbox"
                   class="option-box__check position-absolute left-0 right-0 top-0 bottom-0 m-auto"
                   id="option-none-apply"
                   name="option-none-apply"
                   @focus="onFocus(noneApplyOption)"
                   @blur="onBlur(v$, noneApplyOption)"
                   @invalidity="$emit('invalidity', $event)"
            >

            <div class="boolean-icon-container mt-0 mx-auto mb-3 position-relative">
              <transition name="fade">
                <i v-show="!noneApplySelected" class="icon-none-apply d-inline-block d-inline-block position-absolute top-0 start-0 bottom-0 end-0 m-auto"></i>
              </transition>
              <transition name="fade">
                <i v-show="noneApplySelected" class="icon-none-apply-selected d-inline-block position-absolute top-0 start-0 bottom-0 end-0 m-auto"></i>
              </transition>
            </div>

            <small class="option-box__label">
              None apply
            </small>
          </div>
        </label>

        <!-- These empty option boxes are to set a fixed left alignment on the bottom row -->
        <span class="option-box--spacer"></span>
        <span class="option-box--spacer"></span>
        <!-- These empty option boxes are to set a fixed left alignment on the bottom row -->

        <slot name="helpText"><div class="ba-help-text mb-2" :class="{ 'mb-2': errorText || disclaimer }" v-if="helpText" v-html="helpText"></div></slot>
        <slot name="errorText"><div class="ba-error-message mb-2 invalid-feedback d-block" :class="{ 'mb-2': disclaimer }" v-if="errorText" v-html="errorText"></div></slot>
        <slot name="disclaimer"><div class="ba-disclaimer" v-if="disclaimer" v-html="disclaimer"></div></slot>
      </div>
    </div>
    <div v-else id="basicInfoQuestionBooleanIcons">
      <header class="marketplace-header">
        <p class="mb-4">Select all that apply.</p>
      </header>
      <div class="multi-bool-flex d-flex flex-wrap justify-content-between mx-auto">
        <label
          :class="{
            'mb-3': index <= 2 /* margin-bottom for the top row only */
          }"
          v-for="(option, index) in options"
          :key="index"
          :for="'option-' + index"
          :tabindex="index + 1"
          @keydown.enter="onEnter(options)"
          @keydown.space="toggleValue(option, $event)"
        >
          <div class="option-box rounded p-2 position-relative text-center" :class="{ 'option-box--checked': option.value }">
            <input type="checkbox"
                   class="option-box__check position-absolute left-0 right-0 top-0 bottom-0 m-auto"
                   :id="'option-' + index"
                   :name="'option-' + index"
                   :dreams-id="question.attributes[index].alias"
                   @change="changeValues(options)"
                   v-model="option.value">

            <div class="boolean-icon-container mt-0 mx-auto mb-3 position-relative">
              <transition name="fade">
                <i v-show="option.value" class="position-absolute top-0 start-0 bottom-0 end-0 m-auto" :class="getOptionInfo(option).icon2"></i>
              </transition>
              <transition name="fade">
                <i v-show="!option.value" class="position-absolute top-0 start-0 bottom-0 end-0 m-auto" :class="getOptionInfo(option).icon"></i>
              </transition>
            </div>

            <small class="type-400">
              {{ getOptionInfo(option).label }}
            </small>
          </div>
        </label>
        <!-- enable by removing d-none class if UX would rather the option-box than the button -->
        <label
          class="d-none"
          @keydown.space="noneApply(options, $event)"
          @click="noneApply(options, $event)"
        >
          <div class="single-form-option-box option-box text-center"
               :class="{
              'option-box--checked': noneApplySelected,
              'option-box--focused': noneApplyOption.focused,
              'option-box--invalid': v$.options.$error,
            }">
            <input type="checkbox"
                   class="option-box__check position-absolute left-0 right-0 top-0 bottom-0 m-auto"
                   id="option-none-apply"
                   name="option-none-apply"
                   @focus="onFocus(noneApplyOption)"
                   @blur="onBlur(v$, noneApplyOption)"
                   @invalidity="$emit('invalidity', $event)"
            >

            <div class="boolean-icon-container mt-0 mx-auto mb-3 position-relative">
              <transition name="fade">
                <i v-show="!noneApplySelected" class="icon-none-apply d-inline-block position-absolute top-0 start-0 bottom-0 end-0 m-auto"></i>
              </transition>
              <transition name="fade">
                <i v-show="noneApplySelected" class="icon-none-apply-selected d-inline-block position-absolute top-0 start-0 bottom-0 end-0 m-auto"></i>
              </transition>
            </div>

            <small class="option-box__label">
              None apply
            </small>
          </div>
        </label>
        <!-- These empty option boxes are to set a fixed left alignment on the bottom row -->
        <span class="option-box--spacer p-0 h-0 border-0"></span>
        <span class="option-box--spacer p-0 h-0 border-0"></span>
        <!-- These empty option boxes are to set a fixed left alignment on the bottom row -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useQuestionsStore } from '~/store/questions'
import get from 'lodash/get';
import { useVuelidate } from '@vuelidate/core'
import {required} from '@vuelidate/validators'

export default {
  name: 'borrowerAttributeBooleanIcons',
  emits: ['invalidity', 'submit', 'answer'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      options: this.formatToBooleans(),
      errorMessage: '',
      focused: null,
      noneApplyOption: {}
    }
  },
  created: function() {
    this.$watch('v$.$invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
  },
  computed: {
    ...mapState(useQuestionsStore, {
      singleForm: store => get(store, 'singleForm'),
    }),
    noneFocused() {
      return (this.options.filter( o => o.focused === true ).length === 0) && !this.noneApplyOption.focused
    },
    noneApplySelected() {
      return this.question.answered === true && this.options.filter(option => option.value === true).length === 0
    },
    helpText() {
      return this.question.tooltip ? this.question.helpText : ''
    },
    disclaimer() {
      return this.question.disclaimer || ''
    },
    errorText() {
      let errorTxt = ''
      const validityState = get(this.v$, 'options')
      if (validityState.$error) {
        errorTxt = 'Please select an option, or "None apply" if applicable'
      }
      return errorTxt
    },
  },
  validations() {
    let validations = {
      options: {
        required,
        anySelected: (options) => {
          return this.noneApplySelected ||
            (options.filter( o => o.value === true ).length > 0)
        },
      },
    }
    return { ...validations }
  },
  methods: {
    toggleValue (option, event) {
      event.preventDefault()
      option.value = !option.value
      this.changeValues(this.options)
      this.v$.$touch()
      return option
    },
    noneApply (options, event = null) {
      if (event) {
        event.preventDefault()
      }

      options = (options).map((option) => {
        option.value = false
        return option
      })

      this.changeValues(options, true)
    },
    singleFormChangeValues(options) {
      this.v$.$touch()
      this.singleForm && this.noneApplySelected === true
      this.changeValues(options)
    },
    onEnter (options) {
      this.v$.$touch()
      const anyTrue = !!options.filter((opt) => {
        return opt.value
      }).length
      // Prevent people from pressing enter accidentally and submitting 'none apply'
      if (anyTrue) {
        this.changeValues(options, true)
      }
    },
    onFocus (option) {
      option['focused'] = true
    },
    onBlur (v$, option) {
      option['focused'] = false
      setTimeout(() => {
        if (this.noneFocused) {
          v$.$touch()
        }
      }, 0)
    },
    getOptionInfo (option) {
      let dmOptions = this.question.directiveMapping.options
      return dmOptions.find((dmOption) => {
        return dmOption.alias === option.alias
      })
    },
    formatToBooleans () {
      return this.question.attributes.map((question) => {
        let q = {
          value: question.value,
          alias: question.alias
        }
        if (q.value === '1' || q.value === 1 || q.value === true) {
          q.value = true
        } else {
          q.value = false
        }
        return q
      })
    },
    changeValues (options, submitImmediately) {
      this.$emit('answer', { attr: options, submitImmediately })
    }
  },
  props: {
    question: Object,
    invalidity: Object,
    customButton: Object,
  },
  watch: {
    question: {
      // This cannot be an arrow function or it won't have reference to methods
      handler: function (newVal, oldVal) {
        this.options = this.formatToBooleans()
      }
    },
    customButton: {
      handler: function (newVal) {
        if (newVal.clicked === true) {
          this.noneApply(this.options)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.multi-bool-flex {
  max-width: 360px;
}

.multi-bool-single-form {
  // display: flex;
  // flex-flow: wrap;
  max-width: 540px;
  margin: 0 auto 0 auto;
  // justify-content: space-between;
  label {
    @include media-breakpoint-down(sm) {
      width: 47%;
    }
  }
}

.option-box.single-form-option-box {
  @include media-breakpoint-down(sm) {
    width: 100%;
  }
}

$option-margin: 2px;
.option-box, .option-box--spacer {
  width: 100px;
  height: 125px;
  margin: $option-margin;
}

.option-box--spacer {
  height: 0;
  margin: 0 $option-margin;
}

.option-box {
  border: 1px solid $border-100;
  cursor: pointer;
}

.option-box--checked {
  border: 2px solid $blue-300;
}

.option-box--focused {
  box-shadow: 0 0 0 0.25rem rgb(5 194 240 / 25%);
}
.option-box--invalid {
  border-color: $danger;
}

.option-box__check {
  opacity: 0;
}

.boolean-icon-container {
  width: 54px;
  height: 62px;
}

@include fade-in-out;

%lendio-boolean-icon-img {
  display: inline-block;
  background-image: url('~/public/images/boolean-sprite@2x.png');
  background-repeat: no-repeat;
  background-size: 1600px 80px;
}

@mixin lendio-boolean-icon($width: 67px, $height: 64px, $x: 0, $y: 0, $zoom: 0.435) {
  @extend %lendio-boolean-icon-img;
  width: $width;
  height: $height;
  background-position: $x $y;
}

.icon-none-apply {
  width: 51px;
  height: 40px;
  background-position: 0 0;
  background-image: url('~/public/images/icon-none-apply.svg');
  background-repeat: no-repeat;
  //background-size: 1600px 80px;
}

.icon-none-apply-selected {
  width: 51px;
  height: 40px;
  background-position: 0 0;
  background-image: url('~/public/images/icon-none-apply-selected.svg');
  background-repeat: no-repeat;
}

.icon-bool-cc {
  @include lendio-boolean-icon($width:51px, $height:40px, $x:0px, $y:0);  }
.icon-bool-cc-c {
  @include lendio-boolean-icon($width:51px, $height:40px, $x:-80px, $y:0);  }

.icon-bool-bookkeeping {
  @include lendio-boolean-icon($width:53px, $height:51px, $x:-160px, $y:0);  }
.icon-bool-bookkeeping-c {
  @include lendio-boolean-icon($width:53px, $height:51px, $x:-240px, $y:0);  }

.icon-bool-b2b {
  @include lendio-boolean-icon($width:74px, $height:60px, $x:-320px, $y:0);
}
.icon-bool-b2b-c {
  @include lendio-boolean-icon($width:74px, $height:60px, $x:-400px, $y:0);
}

.icon-bool-b2c {
  @include lendio-boolean-icon($width:69px, $height:33px, $x:-480px, $y:0);
}
.icon-bool-b2c-c {
  @include lendio-boolean-icon($width:69px, $height:33px, $x:-560px, $y:0);
}

.icon-bool-b2g {
  @include lendio-boolean-icon($width:75px, $height:54px, $x:-640px, $y:0);
}
.icon-bool-b2g-c {
  @include lendio-boolean-icon($width:75px, $height:54px, $x:-720px, $y:0);
}

.icon-bool-scales {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-800px, $y:0);
}
.icon-bool-scales-c {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-880px, $y:0);
}

.icon-bool-franchise {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-960px, $y:0);
}
.icon-bool-franchise-c {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-1040px, $y:0);
}

.icon-bool-nonprofit {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-1120px, $y:0);
}
.icon-bool-nonprofit-c {
  @include lendio-boolean-icon($width:51px, $height:51px, $x:-1200px, $y:0);
}

.icon-bool-invoice {
  @include lendio-boolean-icon($width:40px, $height:51px, $x:-1280px, $y:0);
}
.icon-bool-invoice-c {
  @include lendio-boolean-icon($width:40px, $height:51px, $x:-1360px, $y:0);
}

.icon-bool-seasonal {
  @include lendio-boolean-icon($width:53px, $height:51px, $x:-1440px, $y:0);
}
.icon-bool-seasonal-c {
  @include lendio-boolean-icon($width:53px, $height:51px, $x:-1520px, $y:0);
}
</style>
